
import * as React from 'react';
import Autoplay from 'embla-carousel-autoplay';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '@/components/ui/carousel';
import { cn } from '@/lib/utils';

interface ImageSlideshowProps {
  images: { src: string; alt: string }[];
  hdImages?: { src: string }[];
  autoplayInterval?: number;
  className?: string;
}

export default function ImageSlideshow({
  images,
  hdImages,
  autoplayInterval = 5000,
  className,
}: ImageSlideshowProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);
  const [lightboxIndex, setLightboxIndex] = React.useState(-1);

  const autoplayPlugin = React.useMemo(
    () => Autoplay({ delay: autoplayInterval, stopOnInteraction: false }),
    [autoplayInterval]
  );

  React.useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());
    api.on('select', () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  React.useEffect(() => {
    if (!api) return;
    const autoplay = api.plugins().autoplay;
    if (!autoplay) return;
    if (lightboxIndex >= 0) {
      autoplay.stop();
    } else {
      autoplay.play();
    }
  }, [api, lightboxIndex]);

  const lightboxSlides = React.useMemo(
    () =>
      images.map((image, index) => ({
        src: hdImages?.[index]?.src ?? image.src,
      })),
    [images, hdImages]
  );

  if (images.length === 0) return null;

  return (
    <div className={cn('w-full', className)}>
      <Carousel
        setApi={setApi}
        opts={{ loop: true, slidesToScroll: 'auto' }}
        plugins={[autoplayPlugin]}
        className="relative"
      >
        <CarouselContent>
          {images.map((image, index) => (
            <CarouselItem key={index} className="basis-full md:basis-1/2">
              <div
                className="overflow-hidden rounded-lg cursor-pointer"
                onClick={() => setLightboxIndex(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto object-contain"
                  loading={index === 0 ? 'eager' : 'lazy'}
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>

        <CarouselPrevious className="left-3 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-0 shadow-md hover:bg-white dark:hover:bg-slate-800" />
        <CarouselNext className="right-3 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-0 shadow-md hover:bg-white dark:hover:bg-slate-800" />

        {count > 1 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {Array.from({ length: count }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  index === current
                    ? 'w-6 bg-primary'
                    : 'w-2 bg-slate-300 dark:bg-slate-600 hover:bg-slate-400 dark:hover:bg-slate-500'
                )}
                onClick={() => api?.scrollTo(index)}
                aria-label={`Ir a imagen ${index + 1}`}
              />
            ))}
          </div>
        )}
      </Carousel>

      <Lightbox
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
        index={lightboxIndex}
        slides={lightboxSlides}
      />
    </div>
  );
}
